/**
 * Page Messagerie instantanée — inspirée WhatsApp Web.
 *
 * Layout : sidebar gauche (convs + contacts + collègues en ligne) + zone chat.
 */
import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MessageSquare, Users, Search, Plus, X, Send,
  Paperclip, Image as ImageIcon, MoreVertical,
  Circle, Bell, BellOff, Star, Reply, Trash2, Pencil,
  Pin, ChevronDown, Check, CheckCheck,
} from 'lucide-react';
import {
  useListerConversationsQuery, useListerContactsQuery,
  useListerPresenceQuery, useChangerStatutMutation,
  useOuvrirDirectMutation, useMarquerLusMutation,
  type ConversationItem, type Statut,
} from '../../services/messagerieChat';
import { ChatView } from './ChatView';
import { NouvelleConversationModal } from './NouvelleConversationModal';
import { BroadcastAdminModal } from './BroadcastAdminModal';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';

const STATUT_LABEL: Record<Statut, string> = {
  online: 'Disponible',
  busy: 'Occupé',
  meeting: 'En réunion',
  away: 'Absent',
  offline: 'Hors ligne',
};
const STATUT_COULEUR: Record<Statut, string> = {
  online: 'bg-emerald-500',
  busy: 'bg-red-500',
  meeting: 'bg-amber-500',
  away: 'bg-slate-400',
  offline: 'bg-slate-300',
};

export default function Messagerie() {
  const [searchParams, setSearchParams] = useSearchParams();
  const convIdParam = searchParams.get('conv');
  const [selectedConvId, setSelectedConvId] = useState<number | null>(
    convIdParam ? parseInt(convIdParam, 10) : null
  );
  const [ongletSidebar, setOngletSidebar] = useState<'convs' | 'contacts' | 'presence'>('convs');
  const [recherche, setRecherche] = useState('');
  const [showNouvelle, setShowNouvelle] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);

  const user = useSelector((s: RootState) => s.auth.user) as any;
  const estAdmin = user && ['admin', 'super_admin', 'directeur'].includes(user.role);

  // Poll conversations toutes les 5s
  const { data: convsData, refetch: refetchConvs } = useListerConversationsQuery(
    { q: recherche || undefined },
    { pollingInterval: 5000 }
  );
  const conversations = convsData?.results || [];

  // Poll présence toutes les 15s
  const { data: presenceData } = useListerPresenceQuery(undefined, {
    pollingInterval: 15000,
  });
  const monStatut: Statut = presenceData?.mon_statut || 'online';
  const [changerStatut] = useChangerStatutMutation();

  const handleSelectConv = (id: number) => {
    setSelectedConvId(id);
    setSearchParams({ conv: String(id) });
  };

  return (
    <div className="flex h-[calc(100vh-56px)] bg-slate-100 dark:bg-gray-900">
      {/* Sidebar gauche */}
      <div className="w-[360px] flex-shrink-0 border-r border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 flex flex-col">
        {/* Header sidebar */}
        <div className="p-3 border-b border-slate-200 dark:border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare size={18} className="text-amber-600" />
            <h1 className="font-bold text-slate-900 dark:text-white">Messagerie</h1>
          </div>
          <div className="flex items-center gap-1">
            {estAdmin && (
              <button
                onClick={() => setShowBroadcast(true)}
                className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                title="Broadcast urgent"
              >
                <Bell size={16} />
              </button>
            )}
            <button
              onClick={() => setShowNouvelle(true)}
              className="p-1.5 text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 rounded"
              title="Nouvelle conversation"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Mon statut */}
        <div className="px-3 py-2 border-b border-slate-200 dark:border-gray-800 flex items-center gap-2 text-xs">
          <div className={`w-2.5 h-2.5 rounded-full ${STATUT_COULEUR[monStatut]}`} />
          <select
            value={monStatut}
            onChange={(e) => changerStatut({ statut: e.target.value as Statut })}
            className="bg-transparent border-0 text-slate-700 dark:text-gray-300 focus:outline-none cursor-pointer flex-1"
          >
            {(['online', 'busy', 'meeting', 'away'] as Statut[]).map(s => (
              <option key={s} value={s}>{STATUT_LABEL[s]}</option>
            ))}
          </select>
          <span className="text-slate-400">
            {presenceData?.total || 0} en ligne
          </span>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-gray-800 text-xs">
          {[
            { k: 'convs', l: `Convos (${conversations.length})` },
            { k: 'contacts', l: 'Contacts' },
            { k: 'presence', l: `En ligne (${presenceData?.total || 0})` },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setOngletSidebar(t.k as any)}
              className={`flex-1 py-2 font-medium border-b-2 ${
                ongletSidebar === t.k
                  ? 'text-amber-600 border-amber-600'
                  : 'text-slate-500 dark:text-gray-500 border-transparent hover:text-slate-800 dark:hover:text-white'
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {/* Recherche */}
        <div className="p-2 border-b border-slate-200 dark:border-gray-800">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2.5 text-slate-400" />
            <input
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher..."
              className="w-full pl-7 pr-2 py-1.5 text-xs bg-slate-100 dark:bg-gray-900 border-0 rounded focus:ring-1 focus:ring-amber-500 text-slate-900 dark:text-white"
            />
          </div>
        </div>

        {/* Contenu selon onglet */}
        <div className="flex-1 overflow-y-auto">
          {ongletSidebar === 'convs' && (
            <ListeConversations
              conversations={conversations}
              selectedId={selectedConvId}
              onSelect={handleSelectConv}
            />
          )}
          {ongletSidebar === 'contacts' && (
            <ListeContacts recherche={recherche} onDirectOpen={handleSelectConv} />
          )}
          {ongletSidebar === 'presence' && (
            <ListePresence data={presenceData?.results || []} onDirectOpen={handleSelectConv} />
          )}
        </div>
      </div>

      {/* Zone chat */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedConvId ? (
          <ChatView
            convId={selectedConvId}
            onClose={() => { setSelectedConvId(null); setSearchParams({}); }}
            onConvChange={refetchConvs}
          />
        ) : (
          <VueVide />
        )}
      </div>

      {showNouvelle && (
        <NouvelleConversationModal
          onClose={() => setShowNouvelle(false)}
          onCreated={(c) => { setShowNouvelle(false); handleSelectConv(c.id); }}
        />
      )}
      {showBroadcast && (
        <BroadcastAdminModal onClose={() => setShowBroadcast(false)} />
      )}
    </div>
  );
}

function ListeConversations({
  conversations, selectedId, onSelect,
}: {
  conversations: ConversationItem[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  if (conversations.length === 0) {
    return (
      <div className="p-6 text-center text-xs text-slate-500 dark:text-gray-500">
        Aucune conversation.<br />
        Cliquez sur <Plus size={11} className="inline" /> pour démarrer.
      </div>
    );
  }
  return (
    <div>
      {conversations.map(c => (
        <button
          key={c.id}
          onClick={() => onSelect(c.id)}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 border-b border-slate-100 dark:border-gray-900 text-left hover:bg-slate-50 dark:hover:bg-gray-900 ${
            selectedId === c.id ? 'bg-amber-50 dark:bg-amber-900/20' : ''
          }`}
        >
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            {c.avatar_url ? (
              <img src={c.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                c.type_conv === 'direct' ? 'bg-slate-400' :
                c.type_conv === 'service' ? 'bg-blue-500' :
                c.type_conv === 'broadcast' ? 'bg-red-500' : 'bg-emerald-500'
              }`}>
                {c.titre_affichage.charAt(0).toUpperCase()}
              </div>
            )}
            {c.epingle && (
              <Pin size={10} className="absolute -top-1 -right-1 text-amber-600" />
            )}
          </div>
          {/* Contenu */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-sm text-slate-900 dark:text-white truncate">
                {c.titre_affichage}
              </div>
              <div className="text-[10px] text-slate-400 shrink-0">
                {formatDateSidebar(c.date_derniere_activite)}
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 mt-0.5">
              <div className="text-[11px] text-slate-500 dark:text-gray-400 truncate">
                {c.dernier_message ? (
                  <>
                    {c.dernier_message.a_moi && <span className="text-slate-400">Vous : </span>}
                    {c.dernier_message.type_msg === 'image' ? '📷 Image'
                      : c.dernier_message.type_msg === 'fichier' ? `📎 ${c.dernier_message.fichier_nom || 'Fichier'}`
                      : c.dernier_message.contenu}
                  </>
                ) : 'Aucun message'}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {c.muted && <BellOff size={11} className="text-slate-400" />}
                {c.nb_non_lus > 0 && (
                  <span className="bg-amber-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                    {c.nb_non_lus > 99 ? '99+' : c.nb_non_lus}
                  </span>
                )}
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ListeContacts({ recherche, onDirectOpen }: {
  recherche: string;
  onDirectOpen: (convId: number) => void;
}) {
  const [role, setRole] = useState('tous');
  const { data } = useListerContactsQuery({ q: recherche || undefined, role: role !== 'tous' ? role : undefined });
  const [ouvrir] = useOuvrirDirectMutation();

  const handleOuvrir = async (userId: number) => {
    try {
      const c = await ouvrir({ user_id: userId }).unwrap();
      onDirectOpen(c.id);
    } catch (e) { console.error(e); }
  };

  const roles = [
    { k: 'tous', l: 'Tous' },
    { k: 'agent', l: 'Scolarité' },
    { k: 'admin', l: 'Admin' },
    { k: 'directeur', l: 'Direction' },
    { k: 'surveillant', l: 'Surveillance' },
    { k: 'formateur', l: 'Formateurs' },
  ];

  return (
    <div>
      <div className="p-2 flex flex-wrap gap-1 border-b border-slate-100 dark:border-gray-900">
        {roles.map(r => (
          <button key={r.k}
            onClick={() => setRole(r.k)}
            className={`px-2 py-0.5 text-[10px] rounded ${
              role === r.k
                ? 'bg-amber-600 text-white'
                : 'bg-slate-100 dark:bg-gray-900 text-slate-600 dark:text-gray-400 hover:bg-slate-200'
            }`}
          >
            {r.l} {data?.stats_par_role[r.k] ? `(${data.stats_par_role[r.k]})` : ''}
          </button>
        ))}
      </div>
      {(data?.results || []).map(c => (
        <button key={c.user_id}
          onClick={() => handleOuvrir(c.user_id)}
          className="w-full flex items-center gap-2.5 px-3 py-2 border-b border-slate-100 dark:border-gray-900 text-left hover:bg-slate-50 dark:hover:bg-gray-900"
        >
          <div className="relative">
            {c.photo_url ? (
              <img src={c.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-slate-300 dark:bg-gray-700 flex items-center justify-center text-white font-bold text-xs">
                {c.nom.charAt(0)}
              </div>
            )}
            <div className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-white dark:ring-gray-950 ${STATUT_COULEUR[c.statut]}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{c.nom}</div>
            <div className="text-[10px] text-slate-500 truncate">
              {c.role_label}
              {c.en_ligne && c.activite && <span className="text-emerald-600"> • {c.activite}</span>}
            </div>
          </div>
        </button>
      ))}
      {(data?.results || []).length === 0 && (
        <div className="p-6 text-center text-xs text-slate-500">Aucun contact.</div>
      )}
    </div>
  );
}

function ListePresence({ data, onDirectOpen }: {
  data: any[];
  onDirectOpen: (convId: number) => void;
}) {
  const [ouvrir] = useOuvrirDirectMutation();
  const handle = async (uid: number) => {
    try {
      const c = await ouvrir({ user_id: uid }).unwrap();
      onDirectOpen(c.id);
    } catch (e) { console.error(e); }
  };
  if (data.length === 0) {
    return <div className="p-6 text-center text-xs text-slate-500">Personne en ligne.</div>;
  }
  return (
    <div>
      {data.map(p => (
        <button key={p.user_id}
          onClick={() => handle(p.user_id)}
          className="w-full flex items-center gap-2.5 px-3 py-2 border-b border-slate-100 dark:border-gray-900 text-left hover:bg-slate-50 dark:hover:bg-gray-900"
        >
          <div className={`w-2 h-2 rounded-full ${STATUT_COULEUR[p.statut as Statut]}`} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{p.user_nom}</div>
            <div className="text-[10px] text-slate-500 truncate">
              {p.role_label} • <span className="text-emerald-600">{p.activite || STATUT_LABEL[p.statut as Statut]}</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function VueVide() {
  return (
    <div className="flex-1 flex items-center justify-center text-center px-6">
      <div>
        <MessageSquare size={48} className="mx-auto text-slate-300 dark:text-gray-700 mb-3" />
        <div className="text-slate-500 dark:text-gray-500 text-sm">
          Sélectionnez une conversation ou un contact
          <br />pour commencer à discuter.
        </div>
      </div>
    </div>
  );
}

function formatDateSidebar(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const diffJ = Math.floor((now.getTime() - d.getTime()) / (86400000));
  if (diffJ < 7) return d.toLocaleDateString('fr-FR', { weekday: 'short' });
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}
